import cn from 'classnames';
import { type FormEvent } from 'react';
import { FormProvider } from 'react-hook-form';
import { EditorPartitions } from '@widgets/programs/index.ts';
import { FormDescriptionEditor } from '@widgets/shared/Forms/DescriptionEditor/index.tsx';
import { useExerciseForm, useExerciseUpload } from '@features/exercises';
import { ArrowShortLeft } from '@shared/assets';
import { useScrollToTop } from '@shared/hooks/useScrollToTop';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import type { VideoType } from '@shared/types/common';
import {
  FormImageAndName,
  IconButton,
  ListBody,
  PageHead,
  PageTitle,
  TextButton,
  UploadProgressCard,
} from '@shared/ui';
import { FormVideo } from '@shared/ui/forms/video/index.ts';
import Loader from '@src/library/components/common/Loader/index.ts';
import * as st from './styles.module.scss';

type PT = {
  isTempExercise?: boolean;
};
export const ExerciseEdit = ({ isTempExercise }: PT) => {
  const {
    exerciseId,
    methods,
    defaultData,
    resetSelectedVideo,
    onFormClose,
    onFormSubmit,
    isExerciseLoading,
    doSaveFormData,
    source,
    _HASH,
    isDisabledSelectionFromLibrary,
  } = useExerciseForm(Boolean(isTempExercise));
  const { upload, uploadStatus, progress, doAbortLoading } = useExerciseUpload(
    exerciseId,
    resetSelectedVideo,
    onFormSubmit
  );

  useScrollToTop();

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    const formValues = methods.getValues();
    if (formValues.module === 0) {
      methods.setError('module', { message: 'Выберите модуль', type: 'custom' });
      return;
    }
    methods.handleSubmit(() => upload(formValues, formValues.module, isTempExercise))();
  };

  const isSubmitDisabled = false;
  const isEditMode = !isNaN(exerciseId);

  // console.log('values:', methods.getValues());
  // console.log('errors:', methods.formState.errors);
  if (isExerciseLoading) {
    return <Loader />;
  }
  return (
    <>
      <PageTitle title={isEditMode ? 'Редактирование видео' : 'Новое видео'} />
      <PageHead
        Left={
          <IconButton variant="dark" onClick={onFormClose}>
            <ArrowShortLeft />
          </IconButton>
        }
        Right={
          uploadStatus === 'loading' ? (
            <TextButton type="submit" form="exerciseEdit" onClick={doAbortLoading}>
              Отмена
            </TextButton>
          ) : (
            <TextButton
              type="submit"
              form="exerciseEdit"
              onClick={doSaveFormData}
              disabled={isSubmitDisabled}
            >
              Готово
            </TextButton>
          )
        }
      />
      <ListBody>
        <FormProvider {...methods}>
          {uploadStatus === 'idle' && (
            <form
              className={cn(shared.forFormsGap20, st.editor)}
              onSubmit={handleFormSubmit}
              id="exerciseEdit"
            >
              <FormImageAndName
                imageError={methods.formState.errors.screenshot_url?.message}
                titleError={methods.formState.errors.title?.message}
                imageValue={defaultData?.screenshot_url}
                titleValue={defaultData?.title || ''}
                onImageChange={(photo) => {
                  methods.setValue('screenshot_url', photo, { shouldDirty: photo !== '' });
                  methods.clearErrors('screenshot_url');
                }}
                onTitleChange={(title) =>
                  methods.setValue('title', title, { shouldDirty: title !== '' })
                }
                placeholder="Название"
                required
              />

              <FormDescriptionEditor
                hash={_HASH}
                onChange={(v) => methods.setValue('description', v)}
                onRedirect={doSaveFormData}
                initData={defaultData?.description}
              />

              {!isTempExercise && <EditorPartitions onRedirect={doSaveFormData} hash={_HASH} />}
              <FormVideo
                isEditMode={isEditMode}
                videoType={source as VideoType}
                isDisabledSelectionFromLibrary={isDisabledSelectionFromLibrary}
              />
            </form>
          )}
          {uploadStatus === 'loading' && (
            <UploadProgressCard
              progress={progress}
              title="Загружаем видео, пожалуйста не закрывайте окно..."
            />
          )}
        </FormProvider>
      </ListBody>
    </>
  );
};
